// Offline-Satz, Format 1 (docs/kasse-offline/SPEZIFIKATION.md §9.2) – Zod-Schemata, Lesen eines Rohsatzes, Hülle der
// Übertragung und der Datei-Sicherung, Nachricht des Geräte-Nachweises. Rein, für Gerät UND Server: dieselbe Prüfung
// im Gerät (Selbstprüfung, Datei) und beim Übertragen (§9.4). Prüfwert, Kette und Signatur rechnet chain.ts/verify.ts
// (AP-L) über `globalThis.crypto.subtle`.
//
// Regeln: nur ganze Zahlen (Cent, Tausendstel, Basispunkte – Kommazahlen lehnt das Schema ab), Texte höchstens
// ENTRY_TEXT_MAX Zeichen (Artikeltexte ENTRY_ITEM_TEXT_MAX), Satz höchstens ENTRY_MAX_BYTES in UTF-8-Bytes, Zahlarten nur
// bar und Karte (O7), unbekannte Felder lehnt das Schema ab. Neue Felder nur mit neuer `formatVersion`; der Server liest
// die aktuelle und die vorige Fassung und wandelt selbst um – ein Satz wird nie wegen seines Alters abgelehnt.
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zReceiptView } from '../../contract/views'
import { DENOMINATIONS, EVENT_KINDS } from '../labels'
import { VORGANGSTYPEN } from '../processData'
import { GV_TYPES, type VorgangRecord } from '../vorgang'
import { ENTRY_ITEM_TEXT_MAX, ENTRY_MAX_BYTES, ENTRY_TEXT_MAX, OFFLINE_BACKUP_FORMAT, OFFLINE_FORMAT, SUBMIT_PAYLOAD_MAX_CHARS } from './limits'
import { zHex64, zIsoDateTime, zPublicKeySpki, type TillProof } from './types'

export { OFFLINE_FORMAT }

/** Fassungen, die der Server liest (aktuelle und vorige); das Gerät meldet seine im Herzschlag (formatVersions). */
export const OFFLINE_FORMAT_VERSIONS: readonly number[] = [OFFLINE_FORMAT]

export const OFFLINE_KINDS = ['vorgang', 'outage_begin', 'outage_end', 'cash_count', 'paper_begin', 'paper_end', 'note'] as const
export type OfflineKind = (typeof OFFLINE_KINDS)[number]

// ---- Bausteine -----------------------------------------------------------------------------------------------------

const zText = z.string().max(ENTRY_TEXT_MAX)
const zItemText = z.string().max(ENTRY_ITEM_TEXT_MAX)
const zBp = z.int().min(0).max(10_000)

/** JSON-Wert ohne Kommazahlen (Vermerke der Ereignisse) */
export type EntryJson = null | boolean | number | string | EntryJson[] | { [key: string]: EntryJson }
const zEntryJson: z.ZodType<EntryJson> = z.lazy(() =>
  z.union([z.null(), z.boolean(), z.int(), zText, z.array(zEntryJson).max(100), z.record(z.string().max(60), zEntryJson)]),
)

// ---- Einzelaufzeichnung (wie pos_vorgang.record, modules/pos/lib/vorgang.ts) -------------------------------------------

const zPriceFinding = z.strictObject({
  baseGrossCents: z.int(),
  baseNetCents: z.int(),
  baseTaxCents: z.int(),
  discountGrossCents: z.int(),
  discountNetCents: z.int(),
  discountTaxCents: z.int(),
})

const zRecordLine = z.strictObject({
  lineNo: z.int().positive(),
  /** ohne Verbindung gibt es keine Gutscheine (O7) */
  voucherNo: z.null(),
  text: zItemText,
  gvTyp: z.enum(GV_TYPES),
  gvName: zText.nullable(),
  itemNo: zText.nullable(),
  gtin: zText.nullable(),
  groupId: zText.nullable(),
  group: zText.nullable(),
  qty: z.int(),
  unit: zText.nullable(),
  unitGross5: z.int(),
  vatKey: z.int(),
  rateBp: zBp,
  grossCents: z.int(),
  netCents: z.int(),
  taxCents: z.int(),
  priceFinding: zPriceFinding.nullable(),
})

const zRecordVat = z.strictObject({ vatKey: z.int(), rateBp: zBp, grossCents: z.int(), netCents: z.int(), taxCents: z.int() })
const zRecordPayment = z.strictObject({ type: z.enum(['Bar', 'Unbar', 'ECKarte', 'Kreditkarte', 'Keine']), name: zText, amountCents: z.int() })
const zRecordReference = z.strictObject({
  lineNo: z.int().positive().nullable(),
  type: z.literal('Transaktion'),
  date: zText.nullable(),
  registerSerial: zText,
  zNo: z.int().nullable(),
  bonId: z.int().positive(),
  vorgangId: zId,
})

/** Einzelaufzeichnung eines Offline-Vorgangs – genau wie später pos_vorgang.record (ohne Kunde: O7). */
export const zOfflineRecord = z.strictObject({
  v: z.literal(1),
  bonTyp: z.enum(VORGANGSTYPEN),
  bonName: zText,
  storno: z.boolean(),
  startedAt: zIsoDateTime,
  finishedAt: zIsoDateTime,
  operator: z.strictObject({ id: zId, name: zText }).nullable(),
  customer: z.null(),
  totalCents: z.int(),
  note: zText.nullable(),
  lines: z.array(zRecordLine).max(1_000),
  vat: z.array(zRecordVat).max(20),
  payments: z.array(zRecordPayment).max(10),
  references: z.array(zRecordReference).max(50),
})
export type OfflineRecord = z.output<typeof zOfflineRecord>
/** Typprüfung: Die Aufzeichnung im Satz IST eine VorgangRecord. */
export const recordOf = (record: OfflineRecord): VorgangRecord => record

/** Bon, wie gedruckt (ReceiptView) – Zeitpunkte als ISO-Text, weil der Satz JSON ist. */
export const zOfflineReceipt = zReceiptView.extend({ startedAt: zIsoDateTime, finishedAt: zIsoDateTime.nullable() })
export type OfflineReceipt = z.output<typeof zOfflineReceipt>

// ---- Körper je Art -------------------------------------------------------------------------------------------------

export const VORGANG_KINDS = ['sale', 'abort', 'cash_transfer', 'cash_in', 'return'] as const
export type VorgangKind = (typeof VORGANG_KINDS)[number]
/** offline: T0 · online_aborted: T1 · online_completed: T2, T3 (§7.2) */
export const CONTINUATIONS = ['offline', 'online_aborted', 'online_completed'] as const
export type Continuation = (typeof CONTINUATIONS)[number]

export const zVorgangBody = z
  .strictObject({
    /** UUIDv7 des Geräts; bei T1/T2/T3 die ID des online begonnenen Vorgangs */
    vorgangId: zId,
    bonId: z.int().positive(),
    kind: z.enum(VORGANG_KINDS),
    continuation: z.enum(CONTINUATIONS),
    /** Entwurf auf dem Server (T1–T3) */
    onlineBasketId: zId.nullable(),
    shiftId: zId,
    operator: z.strictObject({ id: zId, name: zText }),
    startedAt: zIsoDateTime,
    finishedAt: zIsoDateTime,
    /** Eingaben der Zeilenbildung (composeDocument) */
    basket: z
      .strictObject({
        lines: z
          .array(
            z.strictObject({
              itemId: zId,
              unitId: zId,
              qty: z.int().positive(),
              discountBp: zBp,
              unitPriceCents: z.int().nonnegative(),
              pricePerQty: z.int().positive(),
            }),
          )
          .max(500),
        empties: z.array(z.strictObject({ depositTypeId: zId, qty: z.int().positive() })).max(50),
        headerDiscountBp: zBp,
      })
      .nullable(),
    /** angewandte Werte – Grundlage der Übernahme wie gedruckt (§10.4) */
    applied: z.strictObject({
      taxRates: z.array(z.strictObject({ taxCodeId: zId, rateBp: zBp })).max(20),
      deposits: z.array(z.strictObject({ depositTypeId: zId, unitCents: z.int() })).max(100),
      prices: z
        .array(
          z.strictObject({
            unitId: zId,
            priceListId: zId.nullable(),
            priceId: zId.nullable(),
            source: z.enum(['base', 'group', 'promotion', 'manual']),
          }),
        )
        .max(500),
    }),
    /** nur bar und Karte (O7) – Gutschein, Pfandbon oder Sonstige lehnt das Schema ab */
    payments: z
      .array(
        z.strictObject({
          method: z.enum(['cash', 'card']),
          amountCents: z.int(),
          givenCents: z.int().nonnegative().nullable(),
          /** Belegnummer des Terminals (Karte) */
          reference: z.string().max(40).nullable(),
        }),
      )
      .max(6),
    changeCents: z.int().nonnegative(),
    events: z
      .array(
        z.strictObject({
          kind: z.enum(EVENT_KINDS),
          at: zIsoDateTime,
          amountCents: z.int(),
          description: zText,
          reason: zText.nullable(),
          detail: z.record(z.string().max(60), zEntryJson),
        }),
      )
      .max(200),
    ageCheck: z.strictObject({ minAge: z.union([z.literal(16), z.literal(18)]), confirmedAt: zIsoDateTime }).nullable(),
    /** nur kind 'return': Rücknahme gegen einen Offline-Bon derselben Episode auf demselben Gerät */
    returnOf: z
      .strictObject({
        vorgangId: zId,
        bonId: z.int().positive(),
        lines: z.array(z.strictObject({ lineNo: z.int().positive(), qty: z.int().positive() })).min(1).max(500),
        reason: zText,
      })
      .nullable(),
    record: zOfflineRecord,
    /** processDataOf(record) */
    processData: z.string().max(1_000),
    /** der Bon, wie gedruckt (null bei T1-Abbruch ohne Druck) */
    receipt: zOfflineReceipt.nullable(),
    /** vorgesehen für den elektronischen Beleg ab 2028 (F9); heute immer null */
    receiptToken: z.null(),
    /** kind 'cash_transfer' (an den Tresor) bzw. 'cash_in' (vom Tresor) */
    cashMovement: z.strictObject({ amountCents: z.int().positive(), reason: zText }).nullable(),
    note: zText.nullable(),
  })
  .superRefine((body, ctx) => {
    if ((body.kind === 'return') !== (body.returnOf !== null)) {
      ctx.addIssue({ code: 'custom', path: ['returnOf'], message: 'Nur eine Rücknahme verweist auf einen Bon.' })
    }
    const cash = body.kind === 'cash_transfer' || body.kind === 'cash_in'
    if (cash !== (body.cashMovement !== null)) {
      ctx.addIssue({ code: 'custom', path: ['cashMovement'], message: 'Nur ein Geldtransit nennt eine Bargeldbewegung.' })
    }
  })
export type VorgangBody = z.output<typeof zVorgangBody>

export const zOutageBeginBody = z.strictObject({ reason: zText, lage: z.enum(['internet', 'server', 'unknown']), firstFailureAt: zIsoDateTime })
export type OutageBeginBody = z.output<typeof zOutageBeginBody>

export const zOutageEndBody = z.strictObject({
  lastFailureAt: zIsoDateTime,
  vorgangCount: z.int().nonnegative(),
  firstBonId: z.int().positive().nullable(),
  lastBonId: z.int().positive().nullable(),
  reservedTo: z.int().positive().nullable(),
})
export type OutageEndBody = z.output<typeof zOutageEndBody>

export const zCashCountBody = z.strictObject({
  purpose: z.enum(['check', 'day_end']),
  counts: z
    .array(z.strictObject({ denominationCents: z.int().refine((value) => (DENOMINATIONS as readonly number[]).includes(value)), count: z.int().min(0).max(100_000) }))
    .max(DENOMINATIONS.length * 2),
  countedCents: z.int(),
  expectedCents: z.int(),
  /** false: Soll ungewiss (recent fehlt, §11.6) */
  expectedCertain: z.boolean(),
  diffCents: z.int(),
  note: zText.nullable(),
})
export type CashCountBody = z.output<typeof zCashCountBody>

/** ohne Verbindung gedruckte bzw. begonnene Blätter */
export const zPaperBody = z.strictObject({ reason: zText, sheetNos: z.array(z.int().positive()).max(20) })
export type PaperBody = z.output<typeof zPaperBody>

export const NOTE_CODES = ['clock_jump', 'limit_reached', 'storage_low', 'integrity', 'journal_restore', 'pause', 'resume', 'operator_long'] as const
export type NoteCode = (typeof NOTE_CODES)[number]
export const zNoteBody = z.strictObject({ code: z.enum(NOTE_CODES), text: zText })
export type NoteBody = z.output<typeof zNoteBody>

// ---- Satz ----------------------------------------------------------------------------------------------------------

const entryBase = {
  formatVersion: z.literal(OFFLINE_FORMAT),
  tenantId: zId,
  registerId: zId,
  deviceId: zId,
  /** ab 1, lückenlos je Gerät über alle Arten */
  deviceSeq: z.int().positive(),
  /** beim ersten Satz der Wert confirmed_hash der Kopplung (ZERO_HASH) */
  prevHash: zHex64,
  /** Uhr des Geräts, RFC 3339 mit Versatz */
  createdAt: zIsoDateTime,
  clock: z.strictObject({
    estServerAt: zIsoDateTime.nullable(),
    offsetMs: z.int().nullable(),
    lastContactAt: zIsoDateTime,
    flag: z.enum(['ok', 'unverified', 'drift']),
  }),
  /** Pfad des Einstiegsskripts */
  appBuild: z.string().max(200),
  snapshotVersion: zHex64.nullable(),
  /** Prüfwert des Rechte-Teils des Startpakets (§5.2, §5.3) */
  startpackHash: zHex64,
  startpackIssuedAt: zIsoDateTime,
  /** lokale UUIDv7 der Ausfall-Episode */
  outageId: zId,
}

export const zOfflineEntryV1 = z.discriminatedUnion('kind', [
  z.strictObject({ ...entryBase, kind: z.literal('vorgang'), body: zVorgangBody }),
  z.strictObject({ ...entryBase, kind: z.literal('outage_begin'), body: zOutageBeginBody }),
  z.strictObject({ ...entryBase, kind: z.literal('outage_end'), body: zOutageEndBody }),
  z.strictObject({ ...entryBase, kind: z.literal('cash_count'), body: zCashCountBody }),
  z.strictObject({ ...entryBase, kind: z.literal('paper_begin'), body: zPaperBody }),
  z.strictObject({ ...entryBase, kind: z.literal('paper_end'), body: zPaperBody }),
  z.strictObject({ ...entryBase, kind: z.literal('note'), body: zNoteBody }),
])
export type OfflineEntryV1 = z.output<typeof zOfflineEntryV1>
/** aktuelle Fassung; mit einer neuen Fassung wird daraus eine Vereinigung, die vorige wandelt upgradeEntry um */
export type OfflineEntry = OfflineEntryV1
export type OfflineEntryOf<K extends OfflineKind> = Extract<OfflineEntry, { kind: K }>

/** Größe eines Textes in UTF-8-Bytes (Zod zählt Zeichen in UTF-16, die Grenze gilt für Bytes). */
export const utf8Length = (text: string): number => new TextEncoder().encode(text).length

export type EntryParse =
  | { ok: true; entry: OfflineEntry }
  | {
      ok: false
      /** erste gescheiterte Prüfung (Schlüssel des Prüfprotokolls, types.ts ENTRY_CHECK_KEYS) */
      check: 'size' | 'json' | 'format' | 'schema'
      /** Art laut Rohsatz, sonst 'unknown' (pos_offline_entry.kind) */
      kind: OfflineKind | 'unknown'
      /** Fassung laut Rohsatz, sonst 0 */
      formatVersion: number
      /** fertiger deutscher Satz */
      text: string
      /** Pfade der Schemafehler (höchstens 20) */
      issues: string[]
    }

/**
 * Liest einen Rohsatz: Größe, JSON, Fassung, Schema – in dieser Reihenfolge (Stufe B, §9.4). Prüfwert und Signatur
 * prüft verifyEntry (AP-L) über die Bytes von `payload`, nicht über das gelesene Objekt.
 */
export function parseOfflineEntry(payload: string): EntryParse {
  const fail = (check: 'size' | 'json' | 'format' | 'schema', text: string, raw?: unknown, issues: string[] = []): EntryParse => {
    const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
    const kind = (OFFLINE_KINDS as readonly unknown[]).includes(record.kind) ? (record.kind as OfflineKind) : 'unknown'
    const formatVersion = Number.isSafeInteger(record.formatVersion) && (record.formatVersion as number) >= 0 ? (record.formatVersion as number) : 0
    return { ok: false, check, kind, formatVersion, text, issues }
  }
  if (utf8Length(payload) > ENTRY_MAX_BYTES) return fail('size', `Der Satz ist größer als ${ENTRY_MAX_BYTES} Bytes.`)
  let raw: unknown
  try {
    raw = JSON.parse(payload)
  } catch {
    return fail('json', 'Der Satz ist kein lesbares JSON.')
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fail('json', 'Der Satz ist kein Objekt.', raw)
  const version = (raw as { formatVersion?: unknown }).formatVersion
  if (typeof version !== 'number' || !OFFLINE_FORMAT_VERSIONS.includes(version)) {
    return fail('format', `Die Fassung ${String(version)} des Satzes kennt der Server nicht.`, raw)
  }
  const parsed = zOfflineEntryV1.safeParse(raw)
  if (!parsed.success) {
    const issues = parsed.error.issues.slice(0, 20).map((issue) => `${issue.path.join('.') || '(Satz)'}: ${issue.message}`)
    return fail('schema', 'Der Satz entspricht nicht dem Format der Kasse ohne Verbindung.', raw, issues)
  }
  return { ok: true, entry: parsed.data }
}

// ---- Hülle der Übertragung, des nativen Journals und der Datei-Sicherung ----------------------------------------------

/** Ein Satz, wie er übertragen, im Journal angehängt und in der Datei gesichert wird. */
export const zEntryEnvelope = z.strictObject({
  /** kanonisches JSON genau wie signiert (grobe Grenze in Zeichen; die Bytes prüft der Empfänger) */
  payload: z.string().max(SUBMIT_PAYLOAD_MAX_CHARS),
  hash: zHex64,
  /** Base64 r‖s; die genaue Form prüft die Signaturprüfung (falsche Form → Quarantäne statt Ablehnung des Stapels) */
  sig: z.string().max(100),
})
export type EntryEnvelope = z.output<typeof zEntryEnvelope>

/**
 * Datei „Offline-Daten sichern“ (§11.4): Kopf und alle nicht bestätigten sowie die Sätze der letzten 7 Tage. Daneben
 * eine Datei gleichen Namens mit der Endung `.sha256` (Prüfsumme der Datei). Enthält Namen der Kassenkräfte.
 */
export const zOfflineBackupFile = z.strictObject({
  format: z.literal(OFFLINE_BACKUP_FORMAT),
  tenantId: zId,
  registerId: zId,
  deviceId: zId,
  publicKeySpki: zPublicKeySpki,
  confirmedSeq: z.int().nonnegative(),
  confirmedHash: zHex64,
  createdAt: zIsoDateTime,
  entries: z.array(zEntryEnvelope).max(20_000),
})
export type OfflineBackupFile = z.output<typeof zOfflineBackupFile>

/** Dateiname der Sicherung: merxo-offline-<Kasse>-<Datum>-<Folgenummern>.json */
export function backupFileName(registerCode: string, day: string, fromSeq: number, toSeq: number): string {
  const code = registerCode.replace(/[^A-Za-z0-9-]/g, '-')
  return `merxo-offline-${code}-${day}-${fromSeq}-${toSeq}.json`
}
export const backupChecksumName = (fileName: string): string => `${fileName.replace(/\.json$/, '')}.sha256`

// ---- Geräte-Nachweis (§14.1) ---------------------------------------------------------------------------------------

/** Was der Geräteschlüssel signiert: canonicalJson(proofMessage(…)); fehlende Angaben entfallen (canonicalJson). */
export interface TillProofMessage {
  action: string
  registerId: string
  deviceId: string
  nextBonId?: number
  vorgangId?: string
  nonce?: string
  at: string
  inputHash: string
}

/** Die EINE Form der signierten Nachricht – Gerät (proof.ts, AP-L) und Server (claim.ts, AP-R1) nutzen sie beide. */
export function proofMessage(action: string, registerId: string, proof: Omit<TillProof, 'sig'>): TillProofMessage {
  return {
    action,
    registerId,
    deviceId: proof.deviceId,
    ...(proof.nextBonId !== undefined ? { nextBonId: proof.nextBonId } : {}),
    ...(proof.vorgangId !== undefined ? { vorgangId: proof.vorgangId } : {}),
    ...(proof.nonce !== undefined ? { nonce: proof.nonce } : {}),
    at: proof.at,
    inputHash: proof.inputHash,
  }
}

/** Eingabe ohne `till` – Grundlage von `inputHash` (hex(SHA-256(canonicalJson(withoutTill(eingabe))))). */
export function withoutTill<T extends object>(input: T): Omit<T, 'till'> {
  const { till: _till, ...rest } = input as T & { till?: unknown }
  return rest
}
